import { useRef, type Ref, type SubmitEvent } from "react";
import { useTranslation } from "react-i18next";

import { useUpdatePassword } from "@/api/bale";
import { isOrvalError } from "@/compat";
import { Button } from "@/components/Button";
import { CardSubtitleHeader, CardTextContent } from "@/components/Card";
import { Dialog } from "@/components/Dialog";
import { Form } from "@/components/Form";
import { FormField } from "@/components/FormField";
import { Input } from "@/components/Input";
import { Success } from "@/components/Success";

export function ChangePasswordModal({ ref, onClose }: { ref: Ref<HTMLDialogElement>; onClose?: () => void }) {
  const { t } = useTranslation();
  const form = useRef<HTMLFormElement>(null);

  const updatePassword = useUpdatePassword({
    mutation: {
      onSuccess: () => form.current?.reset(),
    },
  });

  async function handleSubmit(event: SubmitEvent<HTMLFormElement>) {
    const passwordField = event.currentTarget.elements.namedItem("password") as HTMLInputElement;
    const confirmPasswordField = event.currentTarget.elements.namedItem("confirm-password") as HTMLInputElement;

    const form = new FormData(event.currentTarget);
    const password = form.get("password")! as string;
    const new_password = form.get("new_password")! as string;
    const confirmPassword = form.get("confirm-password")! as string;
    if (new_password !== confirmPassword) {
      confirmPasswordField.setCustomValidity(t("passwords-do-not-match"));
    }
    if (!event.currentTarget.reportValidity()) return;
    try {
      await updatePassword.mutateAsync({
        data: { password, new_password },
      });
    } catch (error) {
      if (isOrvalError(error) && error.info.code === "CredentialsIncorrect") {
        passwordField.setCustomValidity(t("password-is-incorrect"));
        passwordField.reportValidity();
      }
    }
  }

  return (
    <Dialog ref={ref} onClose={onClose} closeOnOutside className="w-lg">
      <CardSubtitleHeader>{t("change-password")}</CardSubtitleHeader>
      <CardTextContent className="flex flex-col gap-4">
        {updatePassword.isSuccess && <Success>{t("change-password-success")}</Success>}
        <Form ref={form} error={updatePassword.error} className="flex flex-col gap-4" onSubmit={handleSubmit}>
          <FormField title={t("current-password")}>
            <Input type="password" required placeholder={t("current-password")} name="password" />
          </FormField>
          <FormField title={t("new-password")}>
            <Input type="password" required placeholder={t("new-password")} name="new_password" />
          </FormField>
          <FormField title={t("confirm-new-password")}>
            <Input type="password" required placeholder={t("confirm-new-password")} name="confirm-password" />
          </FormField>
          <Button type="submit" attention="submit">
            {t("save")}
          </Button>
        </Form>
      </CardTextContent>
    </Dialog>
  );
}
